import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { Receipt, Plus, Pencil, Trash2, Download, CheckCircle2, Trash, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TIPI_DOC = [
  { value: 'TD06', label: 'TD06 — Parcella professionista' },
  { value: 'TD01', label: 'TD01 — Fattura' },
  { value: 'TD04', label: 'TD04 — Nota di credito' },
  { value: 'TD24', label: 'TD24 — Fattura differita' },
];

const MOD_PAG = [
  { value: 'MP01', label: 'MP01 — Contanti' },
  { value: 'MP02', label: 'MP02 — Assegno' },
  { value: 'MP05', label: 'MP05 — Bonifico' },
  { value: 'MP08', label: 'MP08 — Carta di credito' },
];

const STATO_COLORS = {
  Bozza: 'bg-amber-100 text-amber-700',
  Emessa: 'bg-blue-100 text-blue-700',
  Pagata: 'bg-emerald-100 text-emerald-700',
  Annullata: 'bg-gray-200 text-gray-600',
};

const emptyRiga = { descrizione: '', quantita: 1, prezzo_unitario: 0, aliquota_iva: 22 };
const empty = {
  cliente_id: '', commessa_id: '', tipo_documento: 'TD06',
  data: new Date().toISOString().slice(0, 10),
  righe: [{ ...emptyRiga }],
  aliquota_ritenuta: 20, causale_ritenuta: 'A',
  cassa_previdenza_perc: 4, cassa_previdenza_iva: true,
  bollo_virtuale: 0, modalita_pagamento: 'MP05',
  iban: '', note: ''
};

const fmtEur = (v) => `€ ${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { if (!s) return '-'; try { return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return s; } };

export default function FatturePage() {
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [f, c, co] = await Promise.all([studioApi.getFatture(), studioApi.getClienti(), studioApi.getCommesse()]);
      setItems(f.data); setClienti(c.data); setCommesse(co.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ ...empty, righe: [{ ...emptyRiga }] }); setEditingId(null); setOpen(true); };
  const openEdit = (f) => {
    setForm({ ...empty, ...f, righe: f.righe?.length ? f.righe : [{ ...emptyRiga }] });
    setEditingId(f.id); setOpen(true);
  };
  const updateRiga = (idx, key, val) => {
    const newRighe = [...form.righe];
    newRighe[idx] = { ...newRighe[idx], [key]: val };
    setForm({ ...form, righe: newRighe });
  };
  const addRiga = () => setForm({ ...form, righe: [...form.righe, { ...emptyRiga }] });
  const removeRiga = (idx) => setForm({ ...form, righe: form.righe.filter((_, i) => i !== idx) });

  const calcImponibile = () => form.righe.reduce((s, r) => s + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0), 0);

  const save = async () => {
    if (!form.cliente_id || !form.righe.length || !form.righe[0].descrizione) {
      toast.error('Cliente e almeno una riga sono obbligatori');
      return;
    }
    const payload = {
      ...form,
      commessa_id: form.commessa_id || null,
      righe: form.righe.map((r) => ({
        descrizione: r.descrizione,
        quantita: parseFloat(r.quantita) || 1,
        prezzo_unitario: parseFloat(r.prezzo_unitario) || 0,
        aliquota_iva: parseFloat(r.aliquota_iva) || 0,
      })),
      aliquota_ritenuta: parseFloat(form.aliquota_ritenuta) || 0,
      cassa_previdenza_perc: parseFloat(form.cassa_previdenza_perc) || 0,
      bollo_virtuale: parseFloat(form.bollo_virtuale) || 0,
    };
    try {
      if (editingId) { await studioApi.updateFattura(editingId, payload); toast.success('Aggiornata'); }
      else { await studioApi.createFattura(payload); toast.success('Creata'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Eliminare fattura "${f.numero}"?`)) return;
    try { await studioApi.deleteFattura(f.id); toast.success('Eliminata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const downloadXml = async (f) => {
    try {
      const r = await studioApi.downloadFatturaXml(f.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      const piva = (clienti.find((c) => c.id === f.cliente_id)?.partita_iva || '00000000000').replace(/\s/g, '');
      const prog = (f.numero || 'F-0001').split('-').pop().padStart(5, '0').slice(0, 5);
      a.download = `IT${piva}_${prog}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('XML SDI scaricato — caricalo su Aruba Fatturazione Elettronica');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore: verifica che il Profilo Professionista sia configurato'); }
  };
  const downloadPdf = async (f) => {
    try {
      const r = await studioApi.downloadFatturaPdf(f.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fattura_${f.numero}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF di cortesia scaricato (con timbro e firma)');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Errore generazione PDF');
      toast.error(msg);
    }
  };
  const markEmessa = async (f) => {
    try { await studioApi.markFatturaEmessa(f.id); toast.success('Marcata come Emessa'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const imponibile = calcImponibile();
  const cassa = imponibile * (parseFloat(form.cassa_previdenza_perc) || 0) / 100;
  const baseIva = imponibile + (form.cassa_previdenza_iva ? cassa : 0);
  const aliquotaIva = parseFloat(form.righe?.[0]?.aliquota_iva) || 22;
  const iva = baseIva * aliquotaIva / 100;
  const ritenuta = imponibile * (parseFloat(form.aliquota_ritenuta) || 0) / 100;
  const bollo = parseFloat(form.bollo_virtuale) || 0;
  const totaleDoc = imponibile + cassa + iva - ritenuta + bollo;

  return (
    <div className="space-y-4 p-4 lg:p-6" data-testid="fatture-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><Receipt className="w-6 h-6 text-indigo-600" /><h1 className="text-2xl font-bold">Fatture Elettroniche</h1><Badge>{items.length}</Badge></div>
          <p className="text-sm text-gray-600 mt-1">Genera XML FatturaPA SDI pronto per upload su Aruba Fatturazione (o qualsiasi altro gateway)</p>
        </div>
        <Button onClick={openCreate} data-testid="new-fattura-btn"><Plus className="w-4 h-4 mr-1" /> Nuova fattura</Button>
      </motion.div>

      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
        <p className="text-blue-900"><strong>Come funziona:</strong></p>
        <ol className="list-decimal ml-5 mt-1 text-blue-800 space-y-0.5">
          <li>Configura il <em>Profilo Professionista</em> in Amministrazione (P.IVA, indirizzo, IBAN, ecc.)</li>
          <li>Crea la fattura: cliente, righe, IVA, ritenuta, cassa previdenza</li>
          <li>Clicca <strong>Scarica XML SDI</strong> → file conforme FatturaPA v1.2.2</li>
          <li>Carica l'XML su <strong>Aruba Fatturazione Elettronica</strong> (drag & drop, 5 secondi)</li>
        </ol>
      </div>

      {loading ? <p className="text-gray-500 italic">Caricamento...</p> : (
        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
              <tr>
                <th className="text-left p-2.5">Numero</th>
                <th className="text-left p-2.5">Data</th>
                <th className="text-left p-2.5">Cliente</th>
                <th className="text-left p-2.5">Tipo</th>
                <th className="text-right p-2.5">Imponibile</th>
                <th className="text-right p-2.5">Totale doc</th>
                <th className="text-center p-2.5">Stato</th>
                <th className="text-center p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} className="border-t hover:bg-gray-50" data-testid={`fattura-row-${f.id}`}>
                  <td className="p-2.5 font-mono text-xs text-indigo-700">{f.numero}</td>
                  <td className="p-2.5">{fmtDate(f.data)}</td>
                  <td className="p-2.5">{f.cliente_nome || '-'}</td>
                  <td className="p-2.5"><Badge variant="outline" className="text-xs">{f.tipo_documento}</Badge></td>
                  <td className="p-2.5 text-right font-mono">{fmtEur(f.totali?.imponibile)}</td>
                  <td className="p-2.5 text-right font-mono font-semibold">{fmtEur(f.totali?.totale_documento)}</td>
                  <td className="p-2.5 text-center"><Badge className={`text-xs ${STATO_COLORS[f.stato] || ''}`}>{f.stato || 'Bozza'}</Badge></td>
                  <td className="p-2.5 text-right">
                    <Button size="icon" variant="ghost" onClick={() => downloadXml(f)} title="Scarica XML SDI" data-testid={`download-xml-${f.id}`}><Download className="w-3.5 h-3.5 text-indigo-600" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => downloadPdf(f)} title="Scarica PDF di cortesia (con timbro/firma)" data-testid={`download-pdf-${f.id}`}><FileText className="w-3.5 h-3.5 text-red-600" /></Button>
                    {f.stato !== 'Emessa' && f.stato !== 'Pagata' && (
                      <Button size="icon" variant="ghost" onClick={() => markEmessa(f)} title="Marca come Emessa"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(f)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(f)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="8" className="text-center text-gray-500 italic py-10">Nessuna fattura ancora.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica fattura' : 'Nuova fattura'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Anagrafica */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label>Cliente *</Label>
                <Select value={form.cliente_id || '__none__'} onValueChange={(v) => setForm({ ...form, cliente_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger data-testid="fattura-cliente"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- nessuno --</SelectItem>
                    {clienti.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Commessa (opz.)</Label>
                <Select value={form.commessa_id || '__none__'} onValueChange={(v) => setForm({ ...form, commessa_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Nessuna" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- nessuna --</SelectItem>
                    {commesse.map((c) => <SelectItem key={c.id} value={c.id}>{c.codice} — {c.oggetto}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo documento</Label>
                <Select value={form.tipo_documento} onValueChange={(v) => setForm({ ...form, tipo_documento: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI_DOC.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} /></div>
              <div><Label>Numero (auto se vuoto)</Label><Input value={form.numero || ''} onChange={(e) => setForm({ ...form, numero: e.target.value })} placeholder="F-2026-001" /></div>
            </div>

            {/* Righe */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                <span className="font-semibold text-sm">Righe fattura</span>
                <Button size="sm" variant="outline" onClick={addRiga}><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi riga</Button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <tr>
                    <th className="text-left p-1.5">Descrizione</th>
                    <th className="text-right p-1.5 w-20">Qta</th>
                    <th className="text-right p-1.5 w-28">Prezzo unit.</th>
                    <th className="text-right p-1.5 w-20">IVA %</th>
                    <th className="text-right p-1.5 w-28">Totale</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.righe.map((r, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-1"><Input value={r.descrizione} onChange={(e) => updateRiga(idx, 'descrizione', e.target.value)} data-testid={`riga-desc-${idx}`} /></td>
                      <td className="p-1"><Input type="number" step="0.01" value={r.quantita} onChange={(e) => updateRiga(idx, 'quantita', e.target.value)} className="text-right" /></td>
                      <td className="p-1"><Input type="number" step="0.01" value={r.prezzo_unitario} onChange={(e) => updateRiga(idx, 'prezzo_unitario', e.target.value)} className="text-right" /></td>
                      <td className="p-1"><Input type="number" step="0.5" value={r.aliquota_iva} onChange={(e) => updateRiga(idx, 'aliquota_iva', e.target.value)} className="text-right" /></td>
                      <td className="p-1 text-right font-mono text-sm">{fmtEur((parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0))}</td>
                      <td className="p-1 text-center">
                        {form.righe.length > 1 && <Button size="icon" variant="ghost" onClick={() => removeRiga(idx)}><Trash className="w-3.5 h-3.5 text-red-500" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cassa, Ritenuta, Bollo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 rounded p-3">
              <div>
                <Label className="text-xs">Cassa previdenza %</Label>
                <Input type="number" step="0.5" value={form.cassa_previdenza_perc} onChange={(e) => setForm({ ...form, cassa_previdenza_perc: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Ritenuta acconto %</Label>
                <Input type="number" step="0.5" value={form.aliquota_ritenuta} onChange={(e) => setForm({ ...form, aliquota_ritenuta: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Bollo virtuale (€)</Label>
                <Input type="number" step="0.01" value={form.bollo_virtuale} onChange={(e) => setForm({ ...form, bollo_virtuale: e.target.value })} placeholder="2.00 se >77€" />
              </div>
              <div>
                <Label className="text-xs">Modalità pagamento</Label>
                <Select value={form.modalita_pagamento} onValueChange={(v) => setForm({ ...form, modalita_pagamento: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MOD_PAG.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">IBAN (opz., default dal Profilo Professionista)</Label>
                <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="IT60X..." />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Note / Causale</Label>
                <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
            </div>

            {/* Riepilogo totali */}
            <div className="bg-indigo-50 border border-indigo-200 rounded p-3 font-mono text-sm">
              <div className="grid grid-cols-2 gap-1">
                <div>Imponibile</div><div className="text-right">{fmtEur(imponibile)}</div>
                {cassa > 0 && (<><div>+ Cassa previdenza ({form.cassa_previdenza_perc}%)</div><div className="text-right">{fmtEur(cassa)}</div></>)}
                <div>+ IVA ({aliquotaIva}%)</div><div className="text-right">{fmtEur(iva)}</div>
                {ritenuta > 0 && (<><div>− Ritenuta ({form.aliquota_ritenuta}%)</div><div className="text-right text-red-600">−{fmtEur(ritenuta)}</div></>)}
                {bollo > 0 && (<><div>+ Bollo</div><div className="text-right">{fmtEur(bollo)}</div></>)}
                <div className="border-t pt-1 mt-1 font-bold">Totale documento</div>
                <div className="border-t pt-1 mt-1 text-right font-bold text-indigo-700">{fmtEur(totaleDoc)}</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-fattura-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
